import { CSRF_HEADER, CSRF_HEADER_VALUE, } from '@concours/shared';
import { ApiRequestError, createApiClient, } from './client.js';
export function createAuthSession({ portal, ...apiOptions }) {
    let accessToken = null;
    let state = { status: 'loading', user: null };
    let refreshing = null;
    const listeners = new Set();
    const base = `/auth/${portal}`;
    const raw = createApiClient({ ...apiOptions, getToken: () => accessToken });
    function setState(next) {
        state = next;
        for (const listener of listeners)
            listener(state);
    }
    function applySession(session) {
        accessToken = session.accessToken;
        setState({ status: 'authenticated', user: session.user });
    }
    function clear() {
        accessToken = null;
        setState({ status: 'anonymous', user: null });
    }
    /** Un seul rafraîchissement à la fois ; les appels concurrents attendent le même. */
    function refresh() {
        refreshing ??= raw
            .post(`${base}/refresh`, undefined, {
            headers: { [CSRF_HEADER]: CSRF_HEADER_VALUE },
        })
            .then((session) => {
            applySession(session);
            return true;
        })
            .catch(() => {
            clear();
            return false;
        })
            .finally(() => {
            refreshing = null;
        });
        return refreshing;
    }
    /** Requête authentifiée : 401 → rafraîchissement puis nouvel essai (une seule fois). */
    async function request(method, path, opts = {}) {
        try {
            return await raw.request(method, path, opts);
        }
        catch (error) {
            if (!(error instanceof ApiRequestError) || error.status !== 401 || path.startsWith('/auth/'))
                throw error;
            if (!(await refresh()))
                throw error;
            return raw.request(method, path, opts);
        }
    }
    const api = {
        request,
        get: (path, opts) => request('GET', path, opts),
        post: (path, body, opts) => request('POST', path, { ...opts, body }),
        put: (path, body, opts) => request('PUT', path, { ...opts, body }),
        patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
        delete: (path, opts) => request('DELETE', path, opts),
        download: (path, opts) => request('GET', path, { ...opts, responseType: 'blob' }),
    };
    /** Résultat d'un appel qui peut ouvrir une session. */
    function handleLogin(response) {
        if (response.status === 'authenticated')
            applySession(response.session);
        return response;
    }
    return {
        portal,
        api,
        getState: () => state,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        /** Au chargement du portail : tente de retrouver la session via le cookie. */
        async restore() {
            if (state.status === 'authenticated')
                return true;
            return refresh();
        },
        refresh,
        async login(email, password) {
            return handleLogin(await raw.post(`${base}/login`, { email, password }));
        },
        async completeMfa(challenge, code) {
            return handleLogin(await raw.post(`${base}/mfa/verify`, { challenge, code }));
        },
        /** Inscription / vérification / invitation renvoient aussi une session. */
        acceptSession(response) {
            return handleLogin(response);
        },
        async reloadUser() {
            const { user } = await api.get(`${base}/me`);
            setState({ status: 'authenticated', user });
            return user;
        },
        async logout() {
            try {
                await raw.post(`${base}/logout`, undefined, {
                    headers: { [CSRF_HEADER]: CSRF_HEADER_VALUE },
                });
            }
            finally {
                clear();
            }
        },
        async logoutEverywhere() {
            try {
                await api.post(`${base}/logout-all`, undefined, {
                    headers: { [CSRF_HEADER]: CSRF_HEADER_VALUE },
                });
            }
            finally {
                clear();
            }
        },
    };
}
//# sourceMappingURL=session.js.map